/**
 * E013-S002: Hook to initialize the DatabaseDiscoveryBridge
 * E012-T015: Cross-Doc Relation Support
 * E012-T018: RBAC Database Visibility Filter
 *
 * This hook should be called once at app startup (e.g., in a root component)
 * to connect the Lit-based RelationConfigMenu with the React-based
 * DatabaseDiscoveryService and CrossDocRelationService.
 *
 * RBAC (E012-T018): Now provides both filtered and unfiltered lists to the bridge.
 * Cross-Doc (E012-T015): Provides CrossDocRelationService for bidirectional relations.
 *
 * Usage:
 * ```tsx
 * // In a root component that has access to services
 * function WorkspaceRoot() {
 *   useDatabaseDiscoveryBridge();
 *   return <children />;
 * }
 * ```
 */

import { useConfirmModal } from '@affine/component';
import { setDatabaseTrashGuard } from '@blocksuite/affine-block-streamlain';
import { useService } from '@toeverything/infra';
import { useEffect } from 'react';

import { AutomationDiscoveryService } from '../../../../modules/streamlain/services/automation-discovery';
import { CrossDocRelationService } from '../../../../modules/streamlain/services/cross-doc-relation';
import { confirmDatabaseDelete } from '../../../../modules/streamlain/services/database-delete-confirm';
import { DatabaseDiscoveryService } from '../../../../modules/streamlain/services/database-discovery';
import {
  clearDatabaseDiscoverySource,
  type DatabaseDiscoveryUpdateDetail,
  findDatabaseByBlockId,
  getDatabaseDiscoveryKey,
  setCrossDocRelationService,
  setDatabaseDiscoverySource,
  type WorkspaceDatabaseInfo,
} from './database-discovery-bridge';

function getDiscoveryFingerprint(database: WorkspaceDatabaseInfo): string {
  return JSON.stringify({
    databaseTitle: database.databaseTitle,
    docTitle: database.docTitle,
    isInTrash: database.isInTrash,
    visible: database.visible,
    columns: database.columns,
  });
}

function getDatabaseSnapshot(databases: WorkspaceDatabaseInfo[]) {
  return new Map(
    databases.map(database => [
      getDatabaseDiscoveryKey(database),
      {
        blockId: database.blockId,
        fingerprint: getDiscoveryFingerprint(database),
      },
    ])
  );
}

function getDiscoveryUpdateDetail(
  previous: Map<string, { blockId: string; fingerprint: string }>,
  next: Map<string, { blockId: string; fingerprint: string }>
): DatabaseDiscoveryUpdateDetail {
  const affectedDatabaseKeys = new Set<string>();
  const affectedDatabaseIds = new Set<string>();

  for (const [key, database] of next) {
    if (previous.get(key)?.fingerprint !== database.fingerprint) {
      affectedDatabaseKeys.add(key);
      affectedDatabaseIds.add(database.blockId);
    }
  }

  for (const [key, database] of previous) {
    if (!next.has(key)) {
      affectedDatabaseKeys.add(key);
      affectedDatabaseIds.add(database.blockId);
    }
  }

  return {
    affectedDatabaseKeys: Array.from(affectedDatabaseKeys),
    affectedDatabaseIds: Array.from(affectedDatabaseIds),
  };
}

type DatabaseDiscoveryLiveData = {
  value: WorkspaceDatabaseInfo[];
  subscribe: (callback: (databases: WorkspaceDatabaseInfo[]) => void) => {
    unsubscribe: () => void;
  };
};

function subscribeToDatabaseDiscoveryUpdates(
  source: DatabaseDiscoveryLiveData
): { unsubscribe: () => void } {
  let previousSnapshot = getDatabaseSnapshot(source.value);
  return source.subscribe(databases => {
    const nextSnapshot = getDatabaseSnapshot(databases);
    const detail = getDiscoveryUpdateDetail(previousSnapshot, nextSnapshot);
    previousSnapshot = nextSnapshot;
    window.dispatchEvent(
      new CustomEvent('database-discovery-updated', { detail })
    );
  });
}

// E018-S008-T005: Extend window for global bridge access
// E018-S008-T006: Extended for DB-Move support
declare global {
  interface Window {
    __databaseBridge?: {
      findDatabaseByBlockId: typeof findDatabaseByBlockId;
      // Column lookup for automation canvas display
      getColumnsForDatabase?: (
        blockId: string
      ) => Array<{ id: string; name: string; type: string }> | null;
      // E018-S008-T006: For DB-Move feature
      findIncomingRelations?: (targetDatabaseId: string) => unknown[];
      refresh?: () => void;
      findAutomationsReferencingDatabase?: (
        databaseBlockId: string
      ) => Array<{ docId: string; blockId: string }>;
    };
    /** Bridge for Lit components to show AFFiNE's native ConfirmModal */
    __confirmModal?: ReturnType<typeof useConfirmModal>;
  }
}

/**
 * Hook to initialize the database discovery bridge.
 * Connects the Lit-based RelationConfigMenu with the workspace-wide
 * DatabaseDiscoveryService and CrossDocRelationService.
 *
 * E012-T015: Provides CrossDocRelationService for cross-doc relations.
 * E012-T018: Provides both RBAC-filtered and unfiltered lists.
 *
 * Should be called once in a component that:
 * 1. Has access to the workspace scope
 * 2. Mounts early in the app lifecycle
 */
export function useDatabaseDiscoveryBridge(): void {
  const discoveryService = useService(DatabaseDiscoveryService);
  const automationDiscoveryService = useService(AutomationDiscoveryService);
  const crossDocRelationService = useService(CrossDocRelationService);
  const confirmModal = useConfirmModal();

  useEffect(() => {
    // Initialize the bridge with all LiveData sources
    // E012-T018: Include RBAC-filtered lists for UI components
    setDatabaseDiscoverySource(
      discoveryService.databases$,
      discoveryService.databasesByDoc$,
      discoveryService.accessibleDatabases$,
      discoveryService.accessibleDatabasesByDoc$,
      discoveryService.writableDatabases$,
      discoveryService.writableDatabasesByDoc$,
      discoveryService.accessibleTrashcanDatabases$
    );

    // E012-T015: Set cross-doc relation service for bidirectional relations
    setCrossDocRelationService(crossDocRelationService);

    // E018-S008-T005: Expose bridge on window for Lit components (Canvas Node)
    // E018-S008-T006: Extended for DB-Move feature
    // This allows runtime database name lookup without React context
    window.__databaseBridge = {
      findDatabaseByBlockId,
      // Column lookup for automation canvas display
      getColumnsForDatabase: (blockId: string) => {
        const db = findDatabaseByBlockId(blockId);
        if (!db) return null;
        return db.columns.map(c => ({ id: c.id, name: c.name, type: c.type }));
      },
      // E018-S008-T006: For DB-Move - find all DBs with relations pointing to a target
      findIncomingRelations: (targetDatabaseId: string) =>
        discoveryService.findIncomingRelations(targetDatabaseId),
      // E018-S008-T006: For DB-Move - refresh cache after move
      refresh: () => discoveryService.refresh(),
      // E018-S008-T006: For DB-Move - find automations referencing a database
      findAutomationsReferencingDatabase: (databaseBlockId: string) =>
        automationDiscoveryService.findAutomationsReferencingDatabase(
          databaseBlockId
        ),
    };

    // Expose confirmModal for Lit components (e.g., RelationConfigMenu dialogs)
    window.__confirmModal = confirmModal;

    // Every soft-delete of a database (canvas menu, delete key, sidebar) runs
    // through this guard so relation dependencies are confirmed first.
    setDatabaseTrashGuard(async ({ store, blockId, displayName }) => {
      const dependencies = await discoveryService.resolveDeleteDependencies(
        blockId,
        store.id
      );
      const docTitle =
        dependencies.target?.docTitle ??
        discoveryService.databases$.value.find(
          database => database.docId === store.id
        )?.docTitle ??
        '';
      return confirmDatabaseDelete(confirmModal, {
        action: 'trash',
        databaseTitle: displayName,
        docTitle,
        dependencies,
      });
    });

    // E018-S008-T005: Subscribe to database changes and emit event for Lit components
    // This triggers re-render of Canvas Nodes when database names or
    // permission-filtered access snapshots change.
    const subscriptions = [
      discoveryService.databases$,
      discoveryService.accessibleDatabases$,
      discoveryService.writableDatabases$,
      discoveryService.accessibleTrashcanDatabases$,
    ].map(source$ => subscribeToDatabaseDiscoveryUpdates(source$));

    return () => {
      // Cleanup on unmount
      for (const subscription of subscriptions) {
        subscription.unsubscribe();
      }
      clearDatabaseDiscoverySource();
      setDatabaseTrashGuard(null);
      delete window.__databaseBridge;
      delete window.__confirmModal;
    };
  }, [
    automationDiscoveryService,
    discoveryService,
    crossDocRelationService,
    confirmModal,
  ]);
}
